import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellRing, BellOff, Siren, Check, Music, Volume2, VolumeX } from 'lucide-react';
import { ALERT_MODES } from '@superchat/shared/constants';
import { useWorkspace } from '../lib/workspace.jsx';
import { usePopover } from '../lib/popover.js';
import SoundSettingsDialog from './SoundSettingsDialog.jsx';

/**
 * How loud the console is, as one choice.
 *
 * These were previously two independent switches — an armed/disarmed alarm and
 * a mute — which left an agent guessing which combination meant what. One list
 * of four named modes says it outright.
 */
export const ALERT_MODE_META = {
  normal: {
    label: 'Normal',
    Icon: Bell,
    hint: 'Every alert sounds — once each.',
  },
  continuous: {
    label: 'Continuous alert',
    Icon: BellRing,
    hint: 'Rings on unread, a visitor arriving, or a chat opening. Stops when you look.',
  },
  critical: {
    label: 'Critical alert',
    Icon: Siren,
    hint: 'Rings only while the visitor is still online and waiting on you.',
  },
  silent: {
    label: 'Silent',
    Icon: BellOff,
    hint: 'No sound at all, including emergencies. Badges still update.',
  },
};

/** Width of the open list, in px. */
const LIST_WIDTH = 262;

/**
 * The speaker button: everything about sound in one place — the quick mute,
 * the alert mode, and which audio files play.
 *
 * It used to be only the mute toggle, with the modes behind the bell. The bell
 * now opens the notification history, so what you *hear* lives under the icon
 * that looks like it.
 */
export function AlertModeMenu() {
  const { alertMode, setAlertMode, isRinging, muted, toggleMute } = useWorkspace();
  const [editingSounds, setEditingSounds] = useState(false);
  const { open, setOpen, spot, box, trigger, list } = usePopover(LIST_WIDTH);

  const current = ALERT_MODE_META[alertMode] ?? ALERT_MODE_META.normal;
  const quiet = muted || alertMode === 'silent';
  const SpeakerIcon = quiet ? VolumeX : Volume2;

  return (
    <div className="alertmenu" ref={box}>
      <button
        type="button"
        ref={trigger}
        className={`icon-button${quiet ? ' is-danger' : alertMode !== 'normal' ? ' is-alert' : ''}${
          isRinging ? ' is-ringing' : ''
        }`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Sound: ${current.label}${muted ? ' · muted' : ''} — ${current.hint}`}
      >
        <SpeakerIcon size={14} />
      </button>

      {spot &&
        createPortal(
          <ul
            className="alertmenu__list"
            role="listbox"
            aria-label="Sound and alert mode"
            ref={list}
            style={{ top: spot.top, left: spot.left, width: spot.width }}
          >
            {/* The quick "not now". Kept apart from the modes: an emergency
                still talks over it, where Silent means silent. */}
            <li className="alertmenu__header">
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={muted}
                className={`alertmenu__item${muted ? ' is-active' : ''}`}
                onClick={toggleMute}
              >
                {muted ? (
                  <VolumeX size={13} className="alertmenu__icon" />
                ) : (
                  <Volume2 size={13} className="alertmenu__icon" />
                )}
                <span className="alertmenu__text">
                  <span className="alertmenu__label">
                    {muted ? 'Sounds muted' : 'Mute sounds'}
                  </span>
                  <span className="alertmenu__hint">
                    {muted ? 'Click to unmute.' : 'A quick pause. Emergencies still sound.'}
                  </span>
                </span>
                {muted && <Check size={12} className="alertmenu__check" />}
              </button>
            </li>
            {ALERT_MODES.map((mode) => {
              const meta = ALERT_MODE_META[mode];
              const ModeIcon = meta.Icon;
              const active = mode === alertMode;
              return (
                <li key={mode}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`alertmenu__item${active ? ' is-active' : ''}`}
                    onClick={() => {
                      setAlertMode(mode);
                      setOpen(false);
                    }}
                  >
                    <ModeIcon size={13} className="alertmenu__icon" />
                    <span className="alertmenu__text">
                      <span className="alertmenu__label">{meta.label}</span>
                      <span className="alertmenu__hint">{meta.hint}</span>
                    </span>
                    {active && <Check size={12} className="alertmenu__check" />}
                  </button>
                </li>
              );
            })}
            {/* Which sound, under how often — the two halves of the same
                question, and the only other place an agent tunes this. */}
            <li className="alertmenu__footer">
              <button
                type="button"
                className="alertmenu__item alertmenu__item--sounds"
                onClick={() => {
                  setEditingSounds(true);
                  setOpen(false);
                }}
              >
                <Music size={13} className="alertmenu__icon" />
                <span className="alertmenu__text">
                  <span className="alertmenu__label">Notification sounds</span>
                  <span className="alertmenu__hint">Use your own audio files.</span>
                </span>
              </button>
            </li>
          </ul>,
          document.body
        )}

      {editingSounds && <SoundSettingsDialog onClose={() => setEditingSounds(false)} />}
    </div>
  );
}

export default AlertModeMenu;
